/**
 * `buildCardImageExtension` — the attachment-backed `image` node for the
 * card-description + comment editor surfaces (`surface === 'card'` /
 * `'comment'`). Mirrors `buildWikiImageExtension` but is deliberately
 * decoupled from the wiki `WikiEmbedOrchestrator` (no lightbox / no
 * cancel-upload window events — cards don't mount that orchestrator).
 *
 * Why a durable node at all? A plain `image` node stores a raw signed S3
 * `src` with a ~5-minute TTL, so a description reloaded minutes later
 * renders a broken image. Storing the `attachmentId` lets the NodeView
 * resolve a fresh signed GET URL on mount (and on `<img onError>`), so
 * embeds survive a reload.
 *
 * Attribute shape (on top of the base Image `src` / `alt` / `title`):
 *   - `attachmentId` — UUID of the `attachments` row when the image was
 *     uploaded through the FU-D presign flow. Rides through TipTap JSON
 *     losslessly (the BE `full`-tier validator allows extra attrs on
 *     image nodes and only URL-checks `src`). A legacy toolbar-URL embed
 *     has `attachmentId === null` and renders straight from `src`.
 *   - `uploadingBlobUrl` (transient) — `URL.createObjectURL(file)` set at
 *     drop / pick time so the NodeView paints the bytes immediately with a
 *     spinner overlay while the upload lands in the background. Never
 *     round-trips through HTML / markdown.
 *   - `placeholderId` (transient) — a client-side id the drop hook uses to
 *     locate this node once the upload settles (to swap in the resolved
 *     `attachmentId` + `src`, or to delete the node on failure). Never
 *     round-trips through HTML / markdown.
 *   - `uploadingFailed` (transient) — set by the comment composers when the
 *     upload failed: the node stays in the body as an error state (blob
 *     preview + Retry / Dismiss) instead of vanishing, and the composer
 *     re-targets it by `placeholderId` on Retry. Never round-trips.
 */

import Image from '@tiptap/extension-image'
import { type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'
import { imageMarkdownStorage } from './image-markdown'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

export interface BuildCardImageExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildCardImageExtension({ view }: BuildCardImageExtensionOptions) {
  return Image.extend({
    name: 'image',
    // Guards the copy path against a src-less image — see `image-markdown`.
    addStorage() {
      return { ...this.parent?.(), ...imageMarkdownStorage() }
    },
    // The stock rule only matches `img[src]`, but an attachment-backed
    // image is copied WITHOUT a src (the signed URL expires; the paste
    // re-links by id), so its clipboard HTML would never parse back into a
    // node. The extra rule takes a src-less `<img>` only when it carries
    // one of our attachment ids — an arbitrary src-less image from another
    // site still parses to nothing.
    parseHTML() {
      return [
        ...(this.parent?.() ?? []),
        {
          tag: 'img[data-attachment-id]',
          getAttrs: (el: HTMLElement) =>
            !el.getAttribute('src') && isUuid(el.getAttribute('data-attachment-id')) ? null : false,
        },
      ]
    },
    // BLOCK node (David's call): the image sits tight on its own block with
    // minimal margin — no line-box leading (which an inline image carries)
    // and no flanking empty paragraphs (which read as big blank gaps). The
    // caret goes before/after via the surrounding text lines + a trailing
    // paragraph; ProseMirror's gap cursor covers the exact-edge case.
    addAttributes() {
      const parent = this.parent?.() ?? {}
      return {
        ...parent,
        attachmentId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-attachment-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { attachmentId: string | null }) =>
            attrs.attachmentId ? { 'data-attachment-id': attrs.attachmentId } : {},
        },
        uploadingBlobUrl: {
          // In-memory-only — never round-trips through HTML / markdown.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        placeholderId: {
          // In-memory-only — used by the drop hook to locate the node
          // while the upload is in flight.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        uploadingFailed: {
          // In-memory-only — flips the uploading placeholder into its
          // error state (Retry / Dismiss). Posting is gated while set.
          default: false,
          parseHTML: () => false,
          renderHTML: () => ({}),
        },
      }
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}

export { isUuid as isCardImageAttachmentId }
